import React from "react";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Target,
  Users,
  Building,
  Scale,
  TreePine,
  ArrowRight,
} from "lucide-react";

const AboutPage = () => {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: Scale,
      title: "Fair Share Allocation",
      description:
        "Transparent calculation of land shares based on contribution and valuation.",
    },
    {
      icon: Users,
      title: "Collective Bargaining",
      description:
        "Landowners unite to negotiate better terms with developers.",
    },
    {
      icon: Building,
      title: "Planned Development",
      description:
        "Systematic urban development with proper infrastructure and amenities.",
    },
    {
      icon: TreePine,
      title: "Sustainable Growth",
      description:
        "Eco-friendly development practices for a better future.",
    },
  ];

  const process = [
    {
      title: "Identification",
      description: "Land parcels are identified for pooling based on urban planning needs.",
    },
    {
      title: "Notification",
      description: "Landowners are notified and invited to participate in the pooling scheme.",
    },
    {
      title: "Eligibility Check",
      description: "Verify whether your parcel falls within a notified DDA zone (J, K-I, L, N, P-I, P-II) and meets the 70% contiguity rule.",
    },
    {
      title: "Consortium Formation",
      description: "Eligible landowners form a consortium for collective management.",
    },
    {
      title: "Agreement & Consent",
      description: "Legal agreements are signed with proper consent mechanisms.",
    },
    {
      title: "Development",
      description: "Land is developed with infrastructure, and shares are allocated.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#4E80FF] to-[#2952CC] py-20">
        <div className="container-main">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-white/90 mb-6">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              Delhi &middot; DDA Land Pooling Policy 2018
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
              About Delhi&apos;s Land Pooling Policy
            </h1>
            <p className="text-xl text-white/90">
              Notified by DDA on 11 October 2018, the Land Pooling Policy lets
              Delhi landowners voluntarily pool contiguous parcels within
              notified zones. In return, 60% of developed land is handed back
              to the consortium; 40% funds city-level infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* What is Land Pooling */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                What is Land Pooling?
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Land pooling, also known as land readjustment, is an urban
                development technique where landowners contribute their land
                for development. After development, a portion of the developed
                land is returned to the original owners, while the remaining
                is used for public infrastructure, amenities, and sold to
                cover development costs.
              </p>
              <p className="text-lg text-slate-600 mb-6">
                This approach ensures organized urban development while
                protecting landowners&apos; interests and creating sustainable
                communities with proper infrastructure.
              </p>
              <div className="space-y-3">
                {[
                  "No forced land acquisition",
                  "Fair returns through developed plots",
                  "Proper urban planning and infrastructure",
                  "Transparent process with government oversight",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#4E80FF]" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/2116716/pexels-photo-2116716.jpeg"
                alt="Modern city development"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-slate-50">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Benefits of Land Pooling
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Land pooling offers multiple advantages for landowners, developers,
              and the community.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="card-hover">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-7 h-7 text-[#4E80FF]" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-white">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              The Land Pooling Process
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A step-by-step journey from land identification to developed plots.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {process.map((step, index) => (
              <div key={index} className="relative flex gap-6 pb-10">
                {/* Timeline Line */}
                {index < process.length - 1 && (
                  <div className="absolute left-6 top-12 w-0.5 h-full bg-slate-200" />
                )}
                {/* Step Number */}
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 ${
                    index % 2 === 0 ? "bg-[#4E80FF]" : "bg-[#4E80FF]"
                  }`}
                >
                  {index + 1}
                </div>
                {/* Content */}
                <div className="flex-1 pb-4">
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Framework */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Legal Framework
              </h2>
              <p className="text-lg text-slate-300 mb-6">
                Land pooling in India is governed by various state-level policies
                and the central government&apos;s guidelines. The Delhi Development
                Authority (DDA) implements land pooling under the Land Pooling
                Policy notified in 2018.
              </p>
              <div className="space-y-4">
                {[
                  "DDA Land Pooling Policy 2018",
                  "Delhi Development Act, 1957",
                  "Master Plan for Delhi 2021/2041",
                  "RERA Compliance for Development",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#6B94FF]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-800 rounded-2xl p-8">
              <Target className="w-12 h-12 text-[#6B94FF] mb-4" />
              <h3 className="font-heading text-2xl font-bold mb-4">
                Key Policy Points
              </h3>
              <ul className="space-y-3 text-slate-300">
                <li>• Minimum 2 hectare land required for pooling</li>
                <li>• 60% developed land returned to owners</li>
                <li>• 40% retained for infrastructure & services</li>
                <li>• FAR benefits for participating landowners</li>
                <li>• Timeline: 3-5 years for development</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#4E80FF] to-[#2952CC]">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Pool Your Land?
          </h2>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
            Start your land pooling journey with our AI-powered platform.
            Check eligibility, upload documents, and join a consortium today.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#4E80FF] hover:bg-white/90 text-lg px-8"
            onClick={() => navigate("/register")}
            data-testid="about-cta-btn"
          >
            Get Started
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
